import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TagFilter } from '@/components/ui/TagFilter';
import { EntityTags } from '@/features/tags/EntityTags';
import { useTagAssignments, useTagMatch, useTags } from '@/lib/api/tags';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Diamond } from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api/client';
import { allocationsApi } from '@/features/stock/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  ProjectStatus,
  XkEvent,
  XkEventGroup,
  XkStation,
} from '@/lib/api/types';
import { Skeleton } from '@/components/ui/Skeleton';
import { Pagination } from '@/components/ui/Pagination';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { usePagination } from '@/lib/hooks/usePagination';
import { useT } from '@/lib/i18n/useT';
import { usePlanQuota } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { useFormPanel } from '@/components/layout/formPanelStore';

/**
 * Projects surface for a brand — the active-projects grid, create dialog,
 * quota banner and XLSX export. Extracted from BrandProfilePage so the same
 * surface renders both inside the brand profile's "Projects" tab AND on the
 * standalone /brand/:id/projects page (sidebar "Projects" entry), with a
 * single source of truth.
 *
 * "Project" in the UI maps to an `xk-event-group`. Queries reuse the brand
 * query keys, so when this renders inside BrandProfilePage the cache is
 * shared (no duplicate network fetch).
 */
interface ProjectVM {
  id: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  status: ProjectStatus;
  events: number;
  stations: number;
  xotskies: number;
}

export function ProjectsTab({
  brandId,
  brandPrimary = null,
  brandAccent = null,
}: {
  brandId: number;
  /** Brand colors for the card hairline accent (optional). */
  brandPrimary?: string | null;
  brandAccent?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const openForm = useFormPanel((s) => s.openForm);
  const validId = Number.isFinite(brandId) && brandId > 0;

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const projectQuota = usePlanQuota('xotskiEventGroups');
  // Create no longer pops a modal — it hosts the form in the right panel
  // (assistant rail). Quota-gated: a full plan opens the upgrade dialog.
  const tryOpenCreate = () => {
    if (projectQuota.reached) {
      setUpgradeOpen(true);
      return;
    }
    openForm({
      kind: 'project',
      mode: 'create',
      title: t('brand.newProject'),
      context: { brandId },
      onDone: (g) => {
        const created = g as { id: number } | undefined;
        if (created?.id) navigate(`/projects/${created.id}`);
      },
    });
  };

  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () =>
      api.list<XkEventGroup>('/xk-event-group', { filter: { companyId: brandId } }),
    enabled: validId,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId } }),
    enabled: validId,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId } }),
    enabled: validId,
  });
  const allocsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byBrand(brandId),
    queryFn: () => allocationsApi.list({ companyId: brandId }),
    enabled: validId,
  });

  const projects: ProjectVM[] = useMemo(() => {
    if (!groupsQ.data || !eventsQ.data || !stationsQ.data || !allocsQ.data) return [];
    const eventsByGroup = new Map<number, XkEvent[]>();
    for (const e of eventsQ.data.items) {
      const list = eventsByGroup.get(e.eventGroupId) ?? [];
      list.push(e);
      eventsByGroup.set(e.eventGroupId, list);
    }
    return groupsQ.data.items.map((g) => {
      const events = eventsByGroup.get(g.id) ?? [];
      const eventIds = new Set(events.map((e) => e.id));
      const stations = stationsQ.data!.items.filter((s) => eventIds.has(s.eventId));
      const stationIds = new Set(stations.map((s) => s.id));
      const allocs = allocsQ.data!.items.filter((a) => stationIds.has(a.stationId));
      const xotskies = allocs.reduce((sum, a) => sum + (a.quantity ?? 0), 0);
      return {
        id: g.id,
        name: g.name,
        startDate: g.startDate ?? null,
        endDate: g.endDate ?? null,
        // Backend xk_event_group doesn't carry projectStatus — derive it from
        // the child events' eventStatus + date window. Defaults to 'planning'
        // when the group has no events at all yet.
        status: g.projectStatus ?? deriveProjectStatus(events),
        events: events.length,
        stations: stations.length,
        xotskies,
      };
    });
  }, [groupsQ.data, eventsQ.data, stationsQ.data, allocsQ.data]);

  /*
    Etiquetas del proyecto.

    Vocabulario y asignaciones se piden una vez para toda la rejilla y se
    reparten por tarjeta; una por tarjeta serían tantas peticiones como
    proyectos.
  */
  const [filtroTags, setFiltroTags] = useState<number[]>([]);
  const vocabTags = useTags(brandId, 'project').data ?? [];
  const { ids: casanTags } = useTagMatch(brandId, 'project', filtroTags);

  // `null` = sin filtro. No es lo mismo que `[]`, que es «nada cumple».
  const proyectosVisibles = useMemo(
    () => (casanTags == null ? projects : projects.filter((p) => casanTags.includes(p.id))),
    [projects, casanTags],
  );

  const asignTags =
    useTagAssignments(
      brandId,
      'project',
      useMemo(() => proyectosVisibles.map((p) => p.id), [proyectosVisibles]),
    ).data ?? {};

  const loadingProjects =
    groupsQ.isLoading || eventsQ.isLoading || allocsQ.isLoading;
  const projectsPager = usePagination(proyectosVisibles, 12);

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2
          className="text-lg font-black tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('brand.activeProjects')}
        </h2>
        <div className="flex items-center gap-2">
          <ExportXlsxButton
            filename="projects"
            sheetName="Projects"
            disabled={projects.length === 0}
            rows={() => projects.map(projectVmToExportRow)}
          />
          <button
            type="button"
            data-tour="projects-create"
            onClick={tryOpenCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('brand.newProject')}
          </button>
        </div>
      </header>

      {loadingProjects ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-52 rounded-xl" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-16 text-center">
          <Diamond className="mx-auto mb-4 h-10 w-10 text-[var(--color-xk-text-muted)]" />
          <h3
            className="mb-2 text-lg font-black text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('brand.noProjectsTitle')}
          </h3>
          <p className="mb-5 text-sm text-[var(--color-xk-text-muted)]">
            {t('brand.noProjectsBody')}
          </p>
          <button
            type="button"
            onClick={tryOpenCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-6 py-3 text-sm font-extrabold text-[var(--color-fg-on-accent)] hover:bg-[var(--color-xk-accent-hover)]"
          >
            <Plus className="h-4 w-4" />
            {t('brand.createProject')}
          </button>
        </div>
      ) : (
        <>
          <TagFilter
            className="mb-3"
            vocabulario={vocabTags}
            valor={filtroTags}
            onChange={setFiltroTags}
            resultado={proyectosVisibles.length}
          />
          <div data-tour="projects-list" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projectsPager.pageItems.map((p) => (
              // Las etiquetas van FUERA de la tarjeta: la tarjeta entera es un
              // `<Link>`, y un control interactivo dentro de un enlace navega al
              // pulsarlo — además de ser HTML inválido.
              <div key={p.id} className="flex flex-col gap-1.5">
                <ProjectCard
                  project={p}
                  brandPrimary={brandPrimary}
                  brandAccent={brandAccent}
                />
                <EntityTags
                  companyId={brandId}
                  entityType="project"
                  entityId={p.id}
                  vocabulario={vocabTags}
                  valor={asignTags[p.id] ?? []}
                />
              </div>
            ))}
          </div>
          <Pagination {...projectsPager} noun="projects" />
        </>
      )}

      {/* Create-project dialog */}
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="xotskiEventGroups"
        currentPlanName="Free"
        limit={projectQuota.limit}
      />
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────

const STATUS: Record<ProjectStatus, { color: string; label: string }> = {
  planning: { color: 'var(--color-warn)', label: 'Planning' },
  live: { color: 'var(--color-xk-accent)', label: 'Live' },
  wrapped: { color: 'var(--color-xk-text-muted)', label: 'Wrapped' },
};

/**
 * Derive a project lifecycle status from its events. The backend's
 * xk_event_group entity does NOT expose projectStatus, so we infer:
 *   - any event currently within [startDate, endDate]              → 'live'
 *   - all events with end_date in the past                         → 'wrapped'
 *   - else (future-only, no dates, or empty)                       → 'planning'
 */
function deriveProjectStatus(events: XkEvent[]): ProjectStatus {
  if (events.length === 0) return 'planning';
  const now = Date.now();
  let hasLive = false;
  let allWrapped = true;
  for (const e of events) {
    if (e.eventStatus !== 'Active') continue;
    const start = e.startDate ? new Date(e.startDate).getTime() : null;
    const end = e.endDate ? new Date(e.endDate).getTime() : null;
    if (start != null && end != null && now >= start && now <= end) hasLive = true;
    if (end != null && now <= end) allWrapped = false;
    if (start == null || end == null) allWrapped = false;
  }
  if (hasLive) return 'live';
  if (allWrapped) return 'wrapped';
  return 'planning';
}

function ProjectCard({
  project,
  brandPrimary,
  brandAccent,
}: {
  project: ProjectVM;
  brandPrimary?: string | null;
  brandAccent?: string | null;
}) {
  const s = STATUS[project.status] ?? STATUS.planning;
  // Brand identity, kept restrained — accent is only used as a hairline cue,
  // not as a full hero band like the SubBrandCard on Home.
  const accent = brandAccent || 'var(--color-xk-accent)';
  const stripeGradient =
    brandPrimary && brandAccent
      ? `linear-gradient(90deg, ${brandPrimary}, ${brandAccent})`
      : accent;
  return (
    <Link
      to={`/projects/${project.id}`}
      className="group relative block overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] transition-all hover:-translate-y-0.5"
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
      }}
    >
      {/* Hairline accent — thin top stripe in brand colors. */}
      <div className="h-[3px]" style={{ background: stripeGradient }} />
      <div className="relative p-4">
        <span
          className="absolute right-3 top-4 rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#0A0A0A]"
          style={{ backgroundColor: s.color, fontFamily: 'var(--font-display)' }}
        >
          {s.label}
        </span>
        <div
          className="mb-1 pr-16 text-[15px] font-extrabold text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {project.name}
        </div>
        <div className="mb-3 text-[11px] text-[var(--color-xk-text-muted)]">
          {fmtRange(project.startDate, project.endDate)}
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-[var(--color-xk-border)] pt-3">
          <Stat n={project.events} label="Events" />
          <Stat n={project.stations} label="Stations" />
          <Stat n={project.xotskies} label="Xotskies" />
        </div>
      </div>
    </Link>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="text-center">
      <div
        className="text-base font-black text-[var(--color-xk-lavender)] tabular-nums"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {n}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[var(--color-xk-text-muted)]">
        {label}
      </div>
    </div>
  );
}

/** Flatten a project view-model for XLSX export. */
function projectVmToExportRow(p: ProjectVM) {
  return {
    ID: p.id,
    Name: p.name,
    Status: p.status,
    'Start date': p.startDate ?? '',
    'End date': p.endDate ?? '',
    Events: p.events,
    Stations: p.stations,
    Xotskies: p.xotskies,
  };
}

function fmtRange(start?: string | null, end?: string | null): string {
  if (!start) return 'TBD';
  const s = dayjs(start);
  const e = end ? dayjs(end) : null;
  if (!e) return s.format('MMM D, YYYY');
  if (s.month() === e.month() && s.year() === e.year()) {
    return `${s.format('MMM D')}–${e.format('D, YYYY')}`;
  }
  return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
}
